// Plain functions behind the badge admin page: resolving stored definitions,
// filtering and grouping the catalogue, the sweep table, the ranking and the
// holders list.
import { RARITY_CLASSES, TIERS, type RarityClass, type Tier } from "../types";
import { categoryRank, familyLabel, familyRank } from "../collection/badgeFormat";
import type {
  AdminBadge,
  AdminBadgeDoc,
  AdminCopyDoc,
  AdminHolder,
  AdminHolderDoc,
  AdminRankingResponse,
  AdminRankingRow,
  AdminSweepResult,
  BadgeCriteria,
} from "./adminTypes";

// ---------- definitions ----------

/** Shelf of a category when the row has none stored (mirrors the backend's definitions.ts). */
const FAMILY_BY_CATEGORY: Record<string, string> = {
  AOTD_NUMBER: "aotd",
  AOTD_COUNT: "aotd",
  AOTD_QUOTES: "aotd",
  AOTD_STREAK: "aotd",
  AOTD_STREAK_COUNT: "aotd",
  AOTD_FAST: "aotd",
  AOTD_TOP_DAYS: "aotd",
  RACE_WINS: "race",
  RACE_STREAK: "race",
  RACE_TIME: "race",
  GAME_JOINED: "race",
  RECIPE_MAKE: "kitchen",
  COFFEE_RATE_COUNT: "coffee",
  COFFEE_NUMBER: "coffee",
  COFFEE_SHOPS: "coffee",
  HOME_BREW: "coffee",
  CHAT_MESSAGES: "chat",
  VOICE_NOTES: "chat",
  CARD_PLAYS: "cards",
  CARD_LEGENDARY: "cards",
  CARD_JACKPOT: "cards",
  COMMENT_COUNT: "social",
  IDEA_SHIPPED: "community",
  community: "community",
  PRINT_ORIGINAL: "prints",
  PRINT_ON_WALLS: "prints",
  PRINT_FAVORITE: "prints",
  PRINT_CARDS: "prints",
  PRINT_BOUGHT: "prints",
  PRINT_POSTER: "prints",
  SIGN_RANK: "account",
  ACCOUNT_AGE: "account",
  BADGE_COUNT: "account",
};

const NUMBERLESS = new Set(["CARD_LEGENDARY"]);

function toTier(color: unknown): Tier {
  const upper = String(color ?? "").trim().toUpperCase();
  return (TIERS as readonly string[]).includes(upper) ? (upper as Tier) : "PAPER";
}

function toRarity(value: unknown): RarityClass {
  return (RARITY_CLASSES as readonly string[]).includes(String(value)) ? (value as RarityClass) : "common";
}

function numberFromCode(code: string): number | null {
  const m = /_(\d+)$/.exec(code);
  return m ? Number(m[1]) : null;
}

function finite(n: unknown): number | null {
  return typeof n === "number" && Number.isFinite(n) ? n : null;
}

/** A stored Badge row with every badges-3 field filled in, old rows included. */
export function resolveAdminBadge(doc: AdminBadgeDoc): AdminBadge {
  const code = String(doc.code ?? "");
  const category = String(doc.category ?? "");
  const threshold = finite(doc.threshold) ?? (NUMBERLESS.has(category) ? 1 : numberFromCode(code) ?? 1);
  let number: number | null;
  if (doc.number === null) number = null;
  else if (finite(doc.number) !== null) number = doc.number as number;
  else number = NUMBERLESS.has(category) ? null : numberFromCode(code);
  const criteria: BadgeCriteria = doc.criteria === "less" || doc.criteria === "equal" ? doc.criteria : "more";
  const s = doc.stats;
  const stats =
    s && finite(s.holders) !== null
      ? {
          holders: finite(s.holders) ?? 0,
          members: finite(s.members) ?? 0,
          pct: finite(s.pct) ?? 0,
          rarity: toRarity(s.rarity),
          firstEarnedBy: s.firstEarnedBy ? String(s.firstEarnedBy) : null,
          firstEarnedAt: s.firstEarnedAt ?? null,
        }
      : null;
  return {
    id: String(doc._id ?? code),
    code,
    name: doc.name || code,
    description: doc.description ?? "",
    category,
    family: doc.family || FAMILY_BY_CATEGORY[category] || category.toLowerCase() || "other",
    tier: toTier(doc.color),
    number,
    threshold,
    criteria,
    points: finite(doc.value) ?? 0,
    repeatable: doc.repeatable === true && doc.isUnique !== true,
    unique: doc.isUnique === true,
    active: doc.active !== false,
    stats,
  };
}

/** "≥ 50", "≤ 250", "= 1". */
export function thresholdText(b: Pick<AdminBadge, "criteria" | "threshold">): string {
  const sign = b.criteria === "less" ? "≤" : b.criteria === "equal" ? "=" : "≥";
  return `${sign} ${b.threshold.toLocaleString("en-US")}`;
}

/** "50 or more", "250 or less", "exactly 1", for the detail panel. */
export function thresholdWords(b: Pick<AdminBadge, "criteria" | "threshold">): string {
  const n = b.threshold.toLocaleString("en-US");
  if (b.criteria === "less") return `${n} or less`;
  if (b.criteria === "equal") return `exactly ${n}`;
  return `${n} or more`;
}

/** Members holding at least one badge, as the engine last counted them. */
export function membersWithBadges(badges: readonly AdminBadge[]): number {
  return badges.reduce((max, b) => Math.max(max, b.stats?.members ?? 0), 0);
}

// ---------- filters ----------

export interface CatalogueFilters {
  q: string;
  /** "" for every family. */
  family: string;
  /** "" for every tier. */
  tier: string;
  /** Only definitions the engine counted with no holder. */
  nobody: boolean;
  /** Only inactive definitions. */
  inactive: boolean;
}

export const EMPTY_FILTERS: CatalogueFilters = { q: "", family: "", tier: "", nobody: false, inactive: false };

export function hasFilters(f: CatalogueFilters): boolean {
  return !!(f.q.trim() || f.family || f.tier || f.nobody || f.inactive);
}

export function filterBadges(badges: readonly AdminBadge[], f: CatalogueFilters): AdminBadge[] {
  const words = f.q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return badges.filter((b) => {
    if (f.family && b.family !== f.family) return false;
    if (f.tier && b.tier !== f.tier) return false;
    if (f.nobody && !(b.stats && b.stats.holders === 0)) return false;
    if (f.inactive && b.active) return false;
    if (words.length) {
      const hay = `${b.name} ${b.code} ${b.category} ${b.description}`.toLowerCase();
      if (!words.every((w) => hay.includes(w))) return false;
    }
    return true;
  });
}

// ---------- grouping ----------

export interface CategoryGroup {
  category: string;
  badges: AdminBadge[];
}

export interface FamilyGroup {
  family: string;
  label: string;
  categories: CategoryGroup[];
  count: number;
}

/** Family, then category, then threshold: the order of the collection shelves. */
export function groupBadges(badges: readonly AdminBadge[]): FamilyGroup[] {
  const families = new Map<string, Map<string, AdminBadge[]>>();
  for (const b of badges) {
    let cats = families.get(b.family);
    if (!cats) families.set(b.family, (cats = new Map()));
    const list = cats.get(b.category);
    if (list) list.push(b);
    else cats.set(b.category, [b]);
  }
  return [...families.entries()]
    .sort(([a], [b]) => familyRank(a) - familyRank(b) || a.localeCompare(b))
    .map(([family, cats]) => {
      const categories = [...cats.entries()]
        .sort(([a], [b]) => categoryRank(a) - categoryRank(b) || a.localeCompare(b))
        .map(([category, list]) => ({
          category,
          badges: [...list].sort((x, y) => x.threshold - y.threshold || x.code.localeCompare(y.code)),
        }));
      return {
        family,
        label: familyLabel(family),
        categories,
        count: categories.reduce((n, c) => n + c.badges.length, 0),
      };
    });
}

/** Families present, in shelf order, for the family filter. */
export function familyOptions(badges: readonly AdminBadge[]): Array<{ value: string; label: string }> {
  return [...new Set(badges.map((b) => b.family))]
    .sort((a, b) => familyRank(a) - familyRank(b) || a.localeCompare(b))
    .map((f) => ({ value: f, label: familyLabel(f) }));
}

// ---------- sweep ----------

export interface SweepRow {
  code: string;
  copies: number;
  history: number;
  fresh: number;
}

export type SweepSortKey = "code" | "copies" | "fresh" | "history";
export type SortDir = "asc" | "desc";

export function sweepRows(result: AdminSweepResult | null | undefined): SweepRow[] {
  const byCode = result?.byCode ?? {};
  const history = result?.backfillByCode ?? {};
  return Object.entries(byCode).map(([code, n]) => {
    const copies = Number(n) || 0;
    const h = Math.min(copies, Number(history[code]) || 0);
    return { code, copies, history: h, fresh: copies - h };
  });
}

export function sortSweepRows(rows: readonly SweepRow[], key: SweepSortKey, dir: SortDir): SweepRow[] {
  const sign = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (key === "code") return sign * a.code.localeCompare(b.code);
    return sign * (a[key] - b[key]) || a.code.localeCompare(b.code);
  });
}

export interface SweepTotals {
  checked: number | null;
  receiving: number;
  copies: number;
  fresh: number;
  history: number;
}

export function sweepTotals(result: AdminSweepResult): SweepTotals {
  const rows = sweepRows(result);
  const copies = finite(result.awarded) ?? rows.reduce((n, r) => n + r.copies, 0);
  const history = finite(result.backfill) ?? rows.reduce((n, r) => n + r.history, 0);
  return {
    checked: finite(result.usersEvaluated),
    receiving: Object.keys(result.byUser ?? {}).length,
    copies,
    fresh: finite(result.fresh) ?? Math.max(0, copies - history),
    history,
  };
}

/** "RACE_TIME_750 x4, AOTD_COUNT_10" for one member's line. */
export function memberCodesText(codes: readonly string[]): string {
  const counts = new Map<string, number>();
  for (const c of codes) counts.set(c, (counts.get(c) ?? 0) + 1);
  return [...counts.entries()].map(([c, n]) => (n > 1 ? `${c} x${n}` : c)).join(", ");
}

// ---------- holders ----------

function copyBadgeId(copy: AdminCopyDoc): string {
  const ref = copy.badge;
  if (!ref) return "";
  if (typeof ref === "string") return ref;
  return String(ref._id ?? "");
}

function time(iso: string | undefined): number {
  const t = iso ? new Date(iso).getTime() : NaN;
  return Number.isNaN(t) ? NaN : t;
}

/**
 * One row per member holding `badgeId`, with their copy count. Most copies
 * first, then whoever earned it first.
 */
export function summarizeHolders(docs: readonly AdminHolderDoc[] | undefined, badgeId: string): AdminHolder[] {
  const out: AdminHolder[] = [];
  for (const u of docs ?? []) {
    const copies = (u.badges ?? []).filter((c) => copyBadgeId(c) === badgeId);
    if (!copies.length) continue;
    const dates = copies.map((c) => time(c.dateEarned)).filter((t) => !Number.isNaN(t));
    out.push({
      userId: String(u._id),
      username: u.username,
      copies: copies.length,
      historyCopies: copies.filter((c) => c.backfill).length,
      unseen: copies.filter((c) => c.collected === false).length,
      firstEarned: dates.length ? new Date(Math.min(...dates)).toISOString() : null,
      lastEarned: dates.length ? new Date(Math.max(...dates)).toISOString() : null,
      firstEver: copies.some((c) => c.firstEver),
    });
  }
  return out.sort(
    (a, b) =>
      b.copies - a.copies ||
      (a.firstEarned ?? "9999").localeCompare(b.firstEarned ?? "9999") ||
      a.username.localeCompare(b.username)
  );
}

// ---------- ranking ----------

export type RankingSortKey = "rank" | "score" | "copies";

/** Best first: rank ascending, score and copies descending. */
export function defaultRankingDir(key: RankingSortKey): SortDir {
  return key === "rank" ? "asc" : "desc";
}

/** The rows of a ranking response, or none when the answer is not one. */
export function rankingRows(data: AdminRankingResponse | null | undefined): AdminRankingRow[] {
  return Array.isArray(data?.rows) ? data!.rows : [];
}

/** Sorted by one column; ties fall back to rank, then username. */
export function sortRankingRows(
  rows: readonly AdminRankingRow[],
  key: RankingSortKey,
  dir: SortDir
): AdminRankingRow[] {
  const sign = dir === "asc" ? 1 : -1;
  return [...rows].sort(
    (a, b) => sign * (a[key] - b[key]) || a.rank - b.rank || a.username.localeCompare(b.username)
  );
}

/**
 * The place each member would reach by closing their gap: the rank of the
 * next better score (not rank - 1, which is wrong under a tie). null at the top.
 */
export function rankAbove(rows: readonly AdminRankingRow[]): Map<string, number | null> {
  const out = new Map<string, number | null>();
  for (const row of rows) {
    let next: AdminRankingRow | null = null;
    for (const r of rows) if (r.score > row.score && (!next || r.score < next.score)) next = r;
    out.set(row.userId, next ? next.rank : null);
  }
  return out;
}

export interface RankChange {
  tone: "up" | "down" | "flat" | "none";
  /** What the cell shows next to the arrow. */
  text: string;
  /** What a screen reader says. */
  label: string;
}

/** The week-on-week move (previousRank - rank): positive is up. */
export function rankChangeView(change: number | null | undefined): RankChange {
  if (change === null || change === undefined || !Number.isFinite(change)) {
    return { tone: "none", text: "-", label: "no rank a week ago" };
  }
  if (change > 0) return { tone: "up", text: String(change), label: `up ${change} since last week` };
  if (change < 0) return { tone: "down", text: String(-change), label: `down ${-change} since last week` };
  return { tone: "flat", text: "0", label: "no change since last week" };
}
